import assetsBase from '@exodus/assets-base'
import {
  ASSET_TYPES,
  FILTER_LIST_MODES,
  ORDER_TYPES,
  PAYMENT_METHOD_TYPES,
  PROVIDERS,
} from '../constants.js'
import { ERROR_PAYMENT_METHOD_NOT_SUPPORTED } from '../errors.js'
import { filterByCountries, omitUndefinedValues } from '../utils.js'
import sardineAdapter from './adapter.js'
import { ENABLED_ASSETS } from './constants.js'

export * from './constants.js'
export * from './utils.js'
export { default as adapter } from './adapter.js'

const assets = assetsBase.default || assetsBase

const sardineProvider = ({
  assets: assets_ = ENABLED_ASSETS,
  countries = {},
  countriesMode = FILTER_LIST_MODES.blacklist,
  enabled = true,
  paymentMethodTypes,
} = {}) => {
  const limits = new Map()
  const getAssets = async ({ countryCode, paymentMethod, serverClient } = {}) => {
    const fetchAssets = async ({ type }) => {
      if (!type) {
        throw new Error('`type` is required')
      }

      const availableAssets = []
      const isCrypto = type === ASSET_TYPES.crypto
      const assetType = isCrypto ? 'supported-tokens' : 'fiat-currencies'
      const { data: assets } = await serverClient.get(`/providers/sardine/${assetType}`, {
        auth: true,
      })

      const providerPaymentMethod = paymentMethod
        ? sardineAdapter.fromExodusToProviderPaymentMethod(paymentMethod, { paymentMethodTypes })
        : undefined

      for (const asset of assets) {
        const exodusAsset = isCrypto
          ? sardineAdapter.fromProviderToExodusAsset(asset, assets_)
          : undefined

        const assetPaymentMethod = isCrypto
          ? undefined
          : asset.paymentOptions.find((option) => option.subTypes.includes(providerPaymentMethod))

        const isPaymentMethodSupported = isCrypto || !paymentMethod || Boolean(assetPaymentMethod)
        const isCountrySupported =
          isCrypto || !countryCode || asset.supportingCountries.includes(countryCode)

        if ((isCrypto && !exodusAsset) || !isCountrySupported || !isPaymentMethodSupported) {
          continue
        }

        const minAmount = 0
        const minBuyAmount = isCrypto ? minAmount : (assetPaymentMethod?.minAmount ?? minAmount)
        const maxAmount = Number.MAX_SAFE_INTEGER
        const maxBuyAmount = isCrypto ? maxAmount : (assetPaymentMethod?.maxAmount ?? maxAmount)

        if (!isCrypto) {
          limits.set(asset.currencyCode, {
            fromMaxBuyAmount: maxBuyAmount,
            fromMaxSellAmount: maxAmount,
            fromMinBuyAmount: minBuyAmount,
            fromMinSellAmount: minAmount,
            toMaxBuyAmount: maxAmount,
            toMaxSellAmount: maxAmount,
            toMinBuyAmount: minAmount,
            toMinSellAmount: minAmount,
          })
        }

        availableAssets.push({
          minBuyAmount,
          minSellAmount: minAmount,
          maxBuyAmount,
          maxSellAmount: maxAmount,
          isBuySupported: true,
          isSellSupported: false,
          name: isCrypto ? exodusAsset.name : asset.currencyCode,
          type,
        })
      }

      return availableAssets
    }

    const [crypto, fiat] = await Promise.all([
      fetchAssets({ type: ASSET_TYPES.crypto }),
      fetchAssets({ type: ASSET_TYPES.fiat }),
    ])

    return [...crypto, ...fiat]
  }

  const getCountries = async ({ countryCode, regionCode, paymentMethod, serverClient } = {}) => {
    const result = []
    const { data: countries_ } = await serverClient.get('/providers/sardine/geo-coverage', {
      auth: true,
    })

    const providerPaymentMethod = paymentMethod
      ? sardineAdapter.fromExodusToProviderPaymentMethod(paymentMethod, { paymentMethodTypes })
      : undefined

    for (const country of countries_) {
      const isCountrySupported = !filterByCountries(
        countries,
        country.countryCode === countryCode ? [countryCode, regionCode] : country.countryCode,
        {
          mode: countriesMode,
          properties: [ORDER_TYPES.buy],
        }
      )

      if (!isCountrySupported) {
        continue
      }

      const hasStates = Array.isArray(country.states)
      const state = country.states?.find((state) => state.code.split('-').pop() === regionCode)
      const countryPaymentMethod = country.paymentMethods.find((option) =>
        option.subTypes.includes(providerPaymentMethod)
      )

      const isSupported =
        country.isAllowedOnRamp &&
        (!countryCode || !regionCode || !hasStates || !state || state.isAllowedOnRamp) &&
        (!paymentMethod || Boolean(countryPaymentMethod))

      if (!isSupported) {
        continue
      }

      result.push({
        code: country.countryCode,
        isBuySupported: true,
        isSellSupported: false,
        name: country.name,
      })
    }

    return result
  }

  const getOrder = async (id, { serverClient } = {}) => {
    const orderId = encodeURIComponent(id)
    const order = await serverClient.getOrders({ provider: PROVIDERS.sardine, orderId })

    return sardineAdapter.fromProviderToExodusOrder({ ...order, id }, assets_)
  }

  const getQuote = async ({
    countryCode,
    fromAmount,
    fromAsset,
    paymentMethod = PAYMENT_METHOD_TYPES.card,
    serverClient,
    toAsset,
    type = ORDER_TYPES.buy,
    walletAddress,
  } = {}) => {
    const providerPaymentMethod = sardineAdapter.fromExodusToProviderPaymentMethod(paymentMethod, {
      assets: assets_,
      country: countryCode,
      currency: fromAsset,
      paymentMethodTypes,
    })

    if (type !== ORDER_TYPES.buy) {
      throw new Error('Only `buy` orders are accepted')
    }

    if (!providerPaymentMethod) {
      throw new Error(ERROR_PAYMENT_METHOD_NOT_SUPPORTED.message)
    }

    if (limits.size === 0 || !limits.has(fromAsset)) {
      await getAssets({ countryCode, paymentMethod, serverClient })
    }

    const [asset, network] = sardineAdapter.fromExodusToProviderAsset(toAsset, assets_).split('_')
    const fiatLimits = limits.get(fromAsset)

    let quote

    try {
      quote = await serverClient.get('/providers/sardine/quotes', {
        auth: true,
        query: omitUndefinedValues({
          asset_type: asset.toUpperCase(),
          currency: fromAsset,
          network,
          paymentType: providerPaymentMethod,
          total: fromAmount,
          walletAddress,
        }),
      })
    } catch (error) {
      if (error.response?.status !== 400) {
        throw error
      }

      if (error.details?.code === 'AMOUNT_TOO_LOW') {
        fiatLimits.fromMinBuyAmount = error.details.data.minQuote
      }

      if (error.details?.code === 'AMOUNT_TOO_HIGH') {
        fiatLimits.fromMaxBuyAmount = error.details.data.maxQuote
      }
    }

    return sardineAdapter.fromProviderToExodusQuote({
      ...fiatLimits,
      ...quote,
      fromAsset,
      toAsset,
      paymentMethod: providerPaymentMethod,
      cryptoAssetPrecision: assets[toAsset].currency.defaultUnit.power,
      type,
    })
  }

  const getPaymentMethod = (paymentMethod, parameters = Object.create(null)) =>
    sardineAdapter.fromExodusToProviderPaymentMethod(paymentMethod, {
      ...parameters,
      assets: assets_,
      paymentMethodTypes,
    })

  return {
    name: PROVIDERS.sardine,
    enabled,
    getAssets,
    getCountries,
    getPaymentMethod,
    getOrder,
    getQuote,
    orderTypes: [ORDER_TYPES.buy],
  }
}

export default sardineProvider
